import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { useShopifyCartContext } from '../onboarding/OnboardingFlowContainer/ShopifyCartProvider';
import { ApiResult } from '../api';
import { shared } from '../config';
import { OFFER_URL } from './constants';
import { getFormattedOfferAmountByType } from './helpers';

const defaultContextValue = {
  addPromoCode: () => Promise.resolve(null),
  formattedAmount: '50%',
  type: undefined,
  amount: undefined,
  code: undefined,
  message: undefined,
  category: undefined,
  defaultOfferCode: shared.DEFAULT_OFFER_CODE || 'DEFAULT',
};

export const ShopifyOfferContext = createContext(defaultContextValue);

// Custom hook to query offer details
const useQueryOfferDetails = () => {
  const router = useRouter();
  const [queryOfferCode, setQueryOfferCode] = useState(null);

  useEffect(() => {
    if (router.isReady && router.query.offer_code) {
      setQueryOfferCode(router.query.offer_code);
    }
  }, [router.isReady, router.query.offer_code]);

  const { data, error } = useSWR(
    queryOfferCode ? `${OFFER_URL}${queryOfferCode}/` : null, // SHOPIFY todo: update to v4 coupons endpoint
  );

  const [invalidQueryCodes, setInvalidQueryCodes] = useState([]);

  useEffect(() => {
    if (error) {
      setInvalidQueryCodes([...invalidQueryCodes, queryOfferCode]);
      setQueryOfferCode(null);
    }
  }, [error, invalidQueryCodes, queryOfferCode]);

  return {
    queryOfferCode,
    offerDetails: data,
    error,
    invalidQueryCodes,
    isLoading: queryOfferCode ? !error && !data : false,
    setQueryOfferCode,
  };
};

export const ShopifyOfferProvider = ({ children }) => {
  const cartContext = useShopifyCartContext();

  // Ensure cartContext is never null/undefined
  const safeCartContext = cartContext || {
    cartId: null,
    error: false,
    couponCode: null,
    isLoading: true,
    addPromo: () => Promise.resolve(null),
  };

  const {
    cartId,
    error: cartError,
    couponCode: cartOfferCode,
    isLoading: isCartLoading,
    addPromo,
  } = safeCartContext;
  const isCartReady =
    (safeCartContext?.cart_id || safeCartContext?.cart?.cart_id) &&
    !cartError &&
    !isCartLoading;
  const {
    queryOfferCode,
    offerDetails: queryOfferDetails,
    isLoading: isQueryLoading,
    setQueryOfferCode,
  } = useQueryOfferDetails();

  const [invalidCodes, setInvalidCodes] = useState([]);

  const offerCode = useMemo(() => {
    if (isQueryLoading || isCartLoading) {
      return null;
    }

    if (queryOfferDetails) {
      return queryOfferDetails.code || queryOfferCode;
    }

    if (cartOfferCode) {
      return cartOfferCode;
    }

    return shared.DEFAULT_OFFER_CODE;
  }, [
    cartOfferCode,
    isQueryLoading,
    isCartLoading,
    queryOfferDetails,
    queryOfferCode,
  ]);

  const { data: offerDetails } = useSWR(
    offerCode ? `${OFFER_URL}${offerCode}/` : null,
    {
      revalidateIfStale: false,
    },
  );

  useEffect(() => {
    if (
      isCartReady &&
      cartId &&
      offerCode &&
      offerCode !== cartOfferCode &&
      !invalidCodes.includes(offerCode)
    ) {
      addPromo(offerCode).then(result => {
        setQueryOfferCode(null);

        ApiResult.ifError(result, () => {
          setInvalidCodes(codes => [...codes, offerCode]);
        });
      });
    }
  }, [
    cartId,
    cartOfferCode,
    invalidCodes,
    offerCode,
    addPromo,
    setQueryOfferCode,
    isCartReady,
  ]);

  const offerState = useMemo(() => {
    const baseState = {
      addPromoCode: addPromo || (() => Promise.resolve(null)),
      formattedAmount: '50%',
      defaultOfferCode: shared.DEFAULT_OFFER_CODE || 'DEFAULT',
    };

    if (offerDetails) {
      const formattedAmount =
        getFormattedOfferAmountByType(offerDetails) || '50%';
      return {
        ...baseState,
        ...offerDetails,
        formattedAmount,
        defaultOfferCode: shared.DEFAULT_OFFER_CODE || 'DEFAULT',
      };
    }

    return baseState;
  }, [addPromo, offerDetails]);

  return (
    <ShopifyOfferContext.Provider value={offerState}>
      {children}
    </ShopifyOfferContext.Provider>
  );
};

export const useShopifyOfferContext = () => useContext(ShopifyOfferContext);
